import { Toaster } from "@/components/ui/toaster";
import { Toaster as Sonner } from "@/components/ui/sonner";
import { TooltipProvider } from "@/components/ui/tooltip";
import { QueryClient, QueryClientProvider } from "@tanstack/react-query";
import { BrowserRouter, Routes, Route, Navigate } from "react-router-dom";
import NotFound from "./pages/NotFound";
import LiveAuctions from "./pages/LiveAuctions";
import CreateRFP from "./pages/CreateRFP";
import AuctionDetails from "./pages/AuctionDetails";
import SupplierResponse from "./pages/SupplierResponse";
import SupplierBidding from "./pages/SupplierBidding";
import DashboardLayout from "./components/DashboardLayout";
import Dashboard from "./pages/Dashboard";
import NewProposal from "./pages/NewProposal";
import Templates from "./pages/Templates";
import ActiveProposals from "./pages/ActiveProposals";
import CompleteProposals from "./pages/CompleteProposals";
import IncompleteProposals from "./pages/IncompleteProposals";
import AllProposals from "./pages/AllProposals";
import SignIn from "./pages/SignIn";
import ResetPassword from "./pages/ResetPassword";
import BuyerProtectedRoute from "./components/BuyerProtectedRoute";
import SupplierProtectedRoute from "./components/SupplierProtectedRoute";
import { AuthProvider } from "./contexts/AuthContext";
import SupplierDashboardLayout from "./components/SupplierDashboardLayout";
import SupplierHome from "./pages/supplier/SupplierHome";
import SupplierActiveRFPs from "./pages/supplier/SupplierActiveRFPs";
import SupplierSuccessfulProposals from "./pages/supplier/SupplierSuccessfulProposals";
import SupplierUnsuccessfulProposals from "./pages/supplier/SupplierUnsuccessfulProposals";
import SupplierSubmittedProposals from "./pages/supplier/SupplierSubmittedProposals";
import SupplierLiveAuctions from "./pages/supplier/SupplierLiveAuctions";
import SupplierClosedAuctions from "./pages/supplier/SupplierClosedAuctions";
import SupplierRFPView from "./pages/supplier/SupplierRFPView";
import SupplierInactiveRFPs from "./pages/supplier/SupplierInactiveRFPs";
import ReverseAuctionView from "./pages/ReverseAuctionView";
import ProposalDetail from "./pages/ProposalDetail";
import RFPFullView from "./pages/RFPFullView";
import ComingSoon from "./pages/ComingSoon";
import Knowledge from "./pages/Knowledge";

const queryClient = new QueryClient();

const App = () => {
  return (
    <QueryClientProvider client={queryClient}>
      <AuthProvider>
        <TooltipProvider>
          <Toaster />
          <Sonner />
          <BrowserRouter>
            <Routes>
              {/* Root path redirects to signin page */}
              <Route path="/" element={<Navigate to="/signin" replace />} />
              <Route path="/signin" element={<SignIn />} />
              <Route path="/reset-password" element={<ResetPassword />} />
              
              {/* Supplier public routes (no auth required) */}
              <Route path="/rfp/:rfpId/respond/:token" element={<SupplierResponse />} />
              <Route path="/rfp/:rfpId/bid/:token" element={<SupplierBidding />} />
              
              {/* Supplier RFP view - accessible after signing in from SupplierResponse */}
              <Route path="/supplier/rfp/:rfpId/view/:token" element={<SupplierRFPView />} />
              
              {/* Supplier protected routes - dashboard and management pages */}
              <Route path="/supplier" element={<SupplierProtectedRoute><SupplierDashboardLayout /></SupplierProtectedRoute>}>
                <Route path="dashboard" element={<SupplierHome />} />
                <Route path="active" element={<SupplierActiveRFPs />} />
                <Route path="submitted" element={<SupplierSubmittedProposals />} />
                <Route path="live-auctions" element={<SupplierLiveAuctions />} />
                <Route path="closed-auctions" element={<SupplierClosedAuctions />} />
                <Route path="successful" element={<SupplierSuccessfulProposals />} />
                <Route path="unsuccessful" element={<SupplierUnsuccessfulProposals />} />
                <Route path="inactive" element={<SupplierInactiveRFPs />} />
                <Route path="settings" element={<ComingSoon title="Supplier Settings" />} />
              </Route>
              
              {/* Buyer protected routes - only for hamaad_karim@hotmail.com */}
              <Route path="/" element={<BuyerProtectedRoute><DashboardLayout /></BuyerProtectedRoute>}>
                <Route path="dashboard" element={<Dashboard />} />
                <Route path="new" element={<NewProposal />} />
                <Route path="templates" element={<Templates />} />
                <Route path="active" element={<ActiveProposals />} />
                <Route path="complete" element={<CompleteProposals />} />
                <Route path="incomplete" element={<IncompleteProposals />} />
                <Route path="all" element={<AllProposals />} />
                <Route path="reverse-auction" element={<CreateRFP />} />
                <Route path="auctions" element={<LiveAuctions />} />
                <Route path="auctions/:id" element={<AuctionDetails />} />
                <Route path="auctions/:id/bids" element={<ReverseAuctionView />} />
                <Route path="auctions/:id/proposal/:proposalId" element={<ProposalDetail />} />
                <Route path="auctions/:id/rfp" element={<RFPFullView />} />
                {/* Knowledge routes */}
                <Route path="knowledge" element={<Knowledge />} />
                <Route path="knowledge/my-analytics" element={<ComingSoon title="MyAnalytics" />} />
                <Route path="knowledge/teamwise" element={<ComingSoon title="Teamwise Analytics" />} />
                <Route path="knowledge/legal-intel" element={<ComingSoon title="Legal Industry Intel" />} />
                {/* My Firm routes */}
                <Route path="firm/engagements" element={<ComingSoon title="Engagement Letter" />} />
                <Route path="firm/documents" element={<ComingSoon title="Document Repository" />} />
                <Route path="firm/panel" element={<ComingSoon title="Manage Panel" />} />
                {/* Settings */}
                <Route path="settings" element={<ComingSoon title="Settings" />} />
              </Route>
              
              <Route path="*" element={<NotFound />} />
            </Routes>
          </BrowserRouter>
        </TooltipProvider>
      </AuthProvider>
    </QueryClientProvider>
  );
};

export default App;
